/**
 * Synthetic spectrum data source for demo/development.
 * Generates realistic 2.4 GHz spectrum patterns:
 *   - WiFi channel humps at CH1 (2412), CH6 (2437), CH11 (2462)
 *   - Random noise floor variation
 *   - Occasional broadband spikes (simulated interference)
 *   - Bluetooth hopping signals (narrow, random position)
 *
 * Also generates periodic alert events to exercise the full pipeline.
 */

import { NUM_CHANNELS, SPECTRUM_POLL_MS } from './constants';
import { NRF_NUM_CHANNELS, NRF_SAMPLES_PER_CH } from '../types/spectrum';
import { AlertType, ALERT_SEVERITY } from '../types/alerts';
import type { Alert } from '../types/alerts';
import type { SpectrumFrame } from '../types/spectrum';

type FrameListener = (frame: SpectrumFrame) => void;
type AlertListener = (alert: Alert) => void;

// WiFi channel center indices (freq - 2400)
const WIFI_HUMPS = [12, 37, 62]; // CH1=2412, CH6=2437, CH11=2462
const WIFI_WIDTH = 10; // ~20 MHz wide channel

export class DemoSource {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private alertTimer: ReturnType<typeof setTimeout> | null = null;
  private frameListeners = new Set<FrameListener>();
  private alertListeners = new Set<AlertListener>();
  private tick = 0;
  private running = false;

  // Slowly varying parameters for realistic animation
  private wifiPower = [1.2, 0.8, 1.0]; // Per-hump power multiplier
  private noisePhase = 0;
  private spikeCountdown = 0;
  private spikeCenter = 0;

  onFrame(fn: FrameListener): () => void {
    this.frameListeners.add(fn);
    return () => this.frameListeners.delete(fn);
  }

  onAlert(fn: AlertListener): () => void {
    this.alertListeners.add(fn);
    return () => this.alertListeners.delete(fn);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.scheduleFrame();
    this.scheduleAlert();
  }

  stop(): void {
    this.running = false;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.alertTimer) { clearTimeout(this.alertTimer); this.alertTimer = null; }
  }

  private scheduleFrame(): void {
    this.timer = setTimeout(() => {
      if (!this.running) return;
      this.generateFrame();
      this.scheduleFrame();
    }, SPECTRUM_POLL_MS);
  }

  private scheduleAlert(): void {
    // Random alert every 8-20 seconds
    const delay = 8000 + Math.random() * 12000;
    this.alertTimer = setTimeout(() => {
      if (!this.running) return;
      this.generateAlert();
      this.scheduleAlert();
    }, delay);
  }

  private generateFrame(): void {
    this.tick++;
    this.noisePhase += 0.07;

    // Slowly vary WiFi channel powers
    for (let i = 0; i < 3; i++) {
      this.wifiPower[i]! += (Math.random() - 0.5) * 0.1;
      this.wifiPower[i] = Math.max(0.3, Math.min(2.0, this.wifiPower[i]!));
    }

    // Occasional interference spike
    if (this.spikeCountdown > 0) {
      this.spikeCountdown--;
    } else if (Math.random() < 0.02) {
      this.spikeCountdown = 5 + Math.floor(Math.random() * 15);
      this.spikeCenter = Math.floor(Math.random() * NUM_CHANNELS);
    }

    const channels = new Uint8Array(NRF_NUM_CHANNELS);

    for (let ch = 0; ch < NRF_NUM_CHANNELS; ch++) {
      let power = 0;

      // Noise floor — smoothly varying
      const noise = Math.sin(this.noisePhase + ch * 0.1) * 0.3 + 0.3;
      if (Math.random() < noise * 0.3) power = 1;

      // WiFi channel humps (Gaussian-ish shape)
      for (let h = 0; h < WIFI_HUMPS.length; h++) {
        const center = WIFI_HUMPS[h]!;
        const dist = Math.abs(ch - center);
        if (dist < WIFI_WIDTH) {
          const strength = this.wifiPower[h]! * (1 - dist / WIFI_WIDTH);
          if (Math.random() < strength * 0.8) {
            power = NRF_SAMPLES_PER_CH;
          } else if (Math.random() < strength * 0.5) {
            power = Math.max(power, 1);
          }
        }
      }

      // Bluetooth hopping — narrow random blips
      if (Math.random() < 0.01) {
        power = NRF_SAMPLES_PER_CH;
      }

      // Interference spike
      if (this.spikeCountdown > 0) {
        const dist = Math.abs(ch - this.spikeCenter);
        if (dist < 8) {
          const intensity = 1 - dist / 8;
          if (Math.random() < intensity * 0.9) {
            power = NRF_SAMPLES_PER_CH;
          }
        }
      }

      channels[ch] = Math.min(NRF_SAMPLES_PER_CH, power);
    }

    const frame: SpectrumFrame = { channels, receivedAt: Date.now() };
    this.frameListeners.forEach((fn) => fn(frame));
  }

  private generateAlert(): void {
    const types = [
      AlertType.NEW_AP,
      AlertType.SPIKE_24GHZ,
      AlertType.NEW_BLE,
      AlertType.SPIKE_SUBGHZ,
      AlertType.JAMMER,
      AlertType.DEAUTH,
    ];
    const type = types[Math.floor(Math.random() * types.length)]!;

    const alert: Alert = {
      type,
      severity: ALERT_SEVERITY[type],
      wifiApCount: 3 + Math.floor(Math.random() * 8),
      bleDeviceCount: 2 + Math.floor(Math.random() * 12),
      strongestRssi: -30 - Math.floor(Math.random() * 50),
      uptimeMs: this.tick * SPECTRUM_POLL_MS,
      receivedAt: Date.now(),
    };

    this.alertListeners.forEach((fn) => fn(alert));
  }
}

/** Singleton demo source */
export const demoSource = new DemoSource();
